/**
 * validateAndResizeCreative
 *
 * Validates an uploaded creative image against a list of allowed dimensions
 * (typically IAB display sizes). If the image is an exact match, it passes
 * through unchanged. If it is a clean integer or half-integer multiple
 * (1.5x, 2x, 3x, etc.) of an allowed size, it is automatically downscaled
 * to the target size via <canvas>. Otherwise the upload is rejected.
 *
 * Returns { file, width, height, wasResized } on success or throws an Error.
 */

const MAX_SCALE = 4; // accept up to 4x retina assets

/**
 * Find the IAB size that matches the given dimensions, either exactly
 * or as a clean multiple (1.5x, 2x, 3x, etc.).
 * Returns { targetWidth, targetHeight, scale } or null.
 */
function getMatchingIabSize(width, height, validDimensions) {
  // Exact match
  for (const dim of validDimensions) {
    if (width === dim.width && height === dim.height) {
      return { targetWidth: dim.width, targetHeight: dim.height, scale: 1 };
    }
  }

  // Check clean multiples (1.5x, 2x, 3x, 4x)
  for (const dim of validDimensions) {
    const scaleX = width / dim.width;
    const scaleY = height / dim.height;
    // Both axes must scale by the same factor, and it must be a clean 0.5 increment
    if (
      scaleX === scaleY &&
      scaleX > 1 &&
      scaleX <= MAX_SCALE &&
      (scaleX * 2) % 1 === 0 // allows 1.5, 2, 2.5, 3, 3.5, 4
    ) {
      return { targetWidth: dim.width, targetHeight: dim.height, scale: scaleX };
    }
  }

  return null;
}

/**
 * Read pixel dimensions from a File using Image element.
 */
function getFileDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });
}

/**
 * Downscale an image file to target dimensions using <canvas>.
 * Returns a new File object with the resized image.
 */
function resizeImage(file, targetWidth, targetHeight) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      // Use high-quality downscaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to resize image'));
            return;
          }
          const resizedFile = new File([blob], file.name, {
            type: file.type || 'image/png',
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        file.type || 'image/png',
        0.92, // quality for lossy formats
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for resizing'));
    };
    img.src = url;
  });
}

/**
 * Main entry point — validate and optionally resize a creative image.
 *
 * @param {File} file - The uploaded image file
 * @param {Array<{width: number, height: number}>} validDimensions - Allowed IAB sizes
 * @returns {Promise<{file: File, width: number, height: number, wasResized: boolean}>}
 * @throws {Error} If dimensions don't match any allowed size (exact or multiple)
 */
export async function validateAndResizeCreative(file, validDimensions) {
  const { width, height } = await getFileDimensions(file);
  const match = getMatchingIabSize(width, height, validDimensions);

  if (!match) {
    const allowedList = validDimensions.map((d) => `${d.width}×${d.height}`).join(', ');
    throw new Error(
      `Image dimensions (${width}×${height}) are not a standard ad size. ` +
      `Allowed sizes: ${allowedList}. ` +
      `Retina images (2x, 3x) are automatically downscaled.`
    );
  }

  if (match.scale === 1) {
    // Exact match — no resize needed
    return { file, width, height, wasResized: false };
  }

  // Downscale to target IAB size
  const resizedFile = await resizeImage(file, match.targetWidth, match.targetHeight);
  return {
    file: resizedFile,
    width: match.targetWidth,
    height: match.targetHeight,
    wasResized: true,
  };
}
